import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import SurveyResultsContent from "../components/SurveyResultsContent";
import ConfirmationModal from "../components/ConfirmationModal";
import { TableActionColumnHeader } from "../components/TableShell";
import SmartInvitationModal from "../components/SmartInvitationModal";
import { IconInvite, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";

import { apiFetch } from "../shared/api/apiFetch";
import { sendSurveyInvites } from "../shared/api/sendSurveyInvites";
import { downloadCsvTextFromString } from "../shared/utils/csvFileHelpers";
import { buildSurveyIndividualResponsesCsvExportText } from "../shared/utils/surveyIndividualResponsesCsv";

const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");
const { useAuth } = require("../contexts/AuthContext");
const { displayEmailOrDash, isPlaceholderEmail } = require("../shared/utils/displayEmail");

function invitationStatusOf(inv) {
  return (inv?.status || "Pending").trim() || "Pending";
}

function isPendingInvitation(inv) {
  return invitationStatusOf(inv) === "Pending";
}

const TYPE_LABELS = {
  single_choice: "Multiple Choice (Single Select)",
  multi_choice: "Multiple Choice (Multi-Select)",
  rating: "Rating Scale",
  likert: "Likert Scale (Agreement)",
  nps: "NPS (0–10)",
  open_ended: "Open-Ended (Short Text)",
};

function SurveyDetails() {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const { userInfo } = useAuth();
  const account_id = userInfo?.account_id;
  const user_id = userInfo?._id != null ? String(userInfo._id) : "";
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [loadingSurvey, setLoadingSurvey] = useState(true);
  const [surveyError, setSurveyError] = useState("");
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);

  const [activeTab, setActiveTab] = useState("results"); // 'questions' | 'invitations' | 'results' | 'individual'

  const [invitationsLoaded, setInvitationsLoaded] = useState(false);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [invitationError, setInvitationError] = useState("");
  const [invitations, setInvitations] = useState([]);

  const [resultsLoaded, setResultsLoaded] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [resultsError, setResultsError] = useState("");
  const [totalResponses, setTotalResponses] = useState(0);
  const [results, setResults] = useState([]);

  const [individualResponsesLoaded, setIndividualResponsesLoaded] = useState(false);
  const [loadingIndividualResponses, setLoadingIndividualResponses] = useState(false);
  const [individualResponsesError, setIndividualResponsesError] = useState("");
  const [individualResponses, setIndividualResponses] = useState([]);
  const [individualResponsesQuestions, setIndividualResponsesQuestions] = useState([]);

  const [searchParams] = useSearchParams();

  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    mode: "alert",
    confirmLabel: "OK",
    onConfirm: null,
  });

  const [invitationStatusFilter, setInvitationStatusFilter] = useState(""); // '' | 'Pending' | 'Completed'
  const [selectedInvitationIds, setSelectedInvitationIds] = useState([]);
  const [resendLoadingId, setResendLoadingId] = useState(null); // invitation _id | 'bulk' | null

  const [invitationsSort, setInvitationsSort] = useState({ key: null, direction: "asc" });
  const [individualSort, setIndividualSort] = useState({ key: null, direction: "asc" });
  const [exportingIndividualCsv, setExportingIndividualCsv] = useState(false);

  useEffect(() => {
    if (!account_id || !surveyId) {
      setSurveyError("Missing account or survey id.");
      setLoadingSurvey(false);
      return;
    }
    setLoadingSurvey(true);
    setSurveyError("");
    apiFetch(serverUrl + "/getSurveyWithQuestions", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: surveyId }),
    })
      .then((resp) => {
        if (resp.ok) return resp.json();
        return resp.text().then((t) => Promise.reject(new Error(t)));
      })
      .then((data) => {
        setSurvey(data.survey || null);
        const list = Array.isArray(data.questions) ? data.questions : [];
        setQuestions(list);
      })
      .catch((err) => {
        setSurvey(null);
        setQuestions([]);
        setSurveyError(err.message || getMessageForFetchError(err));
      })
      .finally(() => setLoadingSurvey(false));
  }, [account_id, surveyId, serverUrl]);

  // Load invitations when survey is available so tab count is correct without clicking the tab
  useEffect(() => {
    if (invitationsLoaded || !account_id || !surveyId) return;
    setLoadingInvitations(true);
    setInvitationError("");
    apiFetch(serverUrl + "/getSurveyInvitations", {
      method: "POST",
      body: JSON.stringify({ account_id, survey_id: surveyId }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        setInvitations(Array.isArray(data) ? data : []);
        setInvitationsLoaded(true);
      })
      .catch((err) => {
        setInvitations([]);
        setInvitationError(getMessageForFetchError(err));
      })
      .finally(() => setLoadingInvitations(false));
  }, [invitationsLoaded, account_id, surveyId, serverUrl]);

  function reloadInvitationsAfterSend() {
    if (!account_id || !surveyId) return;
    setLoadingInvitations(true);
    setInvitationError("");
    apiFetch(serverUrl + "/getSurveyInvitations", {
      method: "POST",
      body: JSON.stringify({ account_id, survey_id: surveyId }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        setInvitations(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        setInvitations([]);
        setInvitationError(getMessageForFetchError(err));
      })
      .finally(() => setLoadingInvitations(false));
  }

  // Load results when survey is available so tab count is correct without clicking the tab
  useEffect(() => {
    if (resultsLoaded || !account_id || !surveyId) return;
    setLoadingResults(true);
    setResultsError("");
    apiFetch(serverUrl + "/getSurveyResults", {
      method: "POST",
      body: JSON.stringify({ account_id, survey_id: surveyId }),
    })
      .then((resp) => {
        if (resp.ok) return resp.json();
        return resp.text().then((t) => Promise.reject(new Error(t)));
      })
      .then((data) => {
        setTotalResponses(typeof data.total_responses === "number" ? data.total_responses : 0);
        setResults(Array.isArray(data.results) ? data.results : []);
        setResultsLoaded(true);
      })
      .catch((err) => {
        setResultsError(err.message || getMessageForFetchError(err));
      })
      .finally(() => setLoadingResults(false));
  }, [resultsLoaded, account_id, surveyId, serverUrl]);

  // Load individual responses when survey is available (for tab count)
  useEffect(() => {
    if (individualResponsesLoaded || !account_id || !surveyId) return;
    setLoadingIndividualResponses(true);
    setIndividualResponsesError("");
    apiFetch(serverUrl + "/getSurveyIndividualResponses", {
      method: "POST",
      body: JSON.stringify({ account_id, survey_id: surveyId }),
    })
      .then((resp) => {
        if (resp.ok) return resp.json();
        return resp.text().then((t) => Promise.reject(new Error(t)));
      })
      .then((data) => {
        setIndividualResponses(Array.isArray(data.responses) ? data.responses : []);
        setIndividualResponsesQuestions(Array.isArray(data.questions) ? data.questions : []);
        setIndividualResponsesLoaded(true);
      })
      .catch((err) => {
        setIndividualResponsesError(err.message || getMessageForFetchError(err));
      })
      .finally(() => setLoadingIndividualResponses(false));
  }, [individualResponsesLoaded, account_id, surveyId, serverUrl]);

  // Open Results tab when URL has ?tab=results
  useEffect(() => {
    if (searchParams.get("tab") === "results") {
      setActiveTab("results");
    }
  }, [searchParams]);

  const questionsCount = questions.length;
  const invitationsCount = invitations.length;

  function handleInvitationsSort(key) {
    setInvitationsSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  }

  const filteredInvitations = (() => {
    if (!invitationStatusFilter) return invitations;
    return invitations.filter((inv) => invitationStatusOf(inv) === invitationStatusFilter);
  })();

  const sortedInvitations = (() => {
    if (!invitationsSort.key) return filteredInvitations;
    const list = [...filteredInvitations];
    list.sort((a, b) => {
      let aVal = a[invitationsSort.key];
      let bVal = b[invitationsSort.key];
      if (invitationsSort.key === "sent_at" || invitationsSort.key === "responded_at") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (invitationsSort.key === "respondent_display") {
        aVal = (a.respondent_display ?? a.email ?? "").toString().toLowerCase();
        bVal = (b.respondent_display ?? b.email ?? "").toString().toLowerCase();
      } else {
        aVal = (aVal ?? "").toString().toLowerCase();
        bVal = (bVal ?? "").toString().toLowerCase();
      }
      if (aVal < bVal) return invitationsSort.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return invitationsSort.direction === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  })();

  const visiblePendingInvitations = sortedInvitations.filter(isPendingInvitation);
  const selectedPendingInvitations = visiblePendingInvitations.filter((inv) =>
    selectedInvitationIds.includes(inv._id)
  );
  const allVisiblePendingSelected =
    visiblePendingInvitations.length > 0 &&
    visiblePendingInvitations.every((inv) => selectedInvitationIds.includes(inv._id));
  const resendBusy = resendLoadingId != null;

  function toggleInvitationSelected(id) {
    setSelectedInvitationIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function toggleSelectAllPendingVisible() {
    if (allVisiblePendingSelected) {
      const visibleIds = new Set(visiblePendingInvitations.map((inv) => inv._id));
      setSelectedInvitationIds((prev) => prev.filter((id) => !visibleIds.has(id)));
      return;
    }
    setSelectedInvitationIds((prev) =>
      Array.from(new Set([...prev, ...visiblePendingInvitations.map((inv) => inv._id)]))
    );
  }

  function clearInvitationSelection() {
    setSelectedInvitationIds([]);
  }

  function closeConfirmDialog() {
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
  }

  function showSendResultMessage(message) {
    setConfirmDialog({
      open: true,
      title: "Send survey",
      message: message || "Invites processed.",
      mode: "alert",
      confirmLabel: "OK",
      onConfirm: null,
    });
  }

  async function sendResendInvites(emails) {
    const unique = Array.from(
      new Set((emails || []).map((e) => (e != null ? String(e).trim() : "")).filter(Boolean))
    );
    if (unique.length === 0) {
      throw new Error("No pending recipients to re-send.");
    }
    const data = await sendSurveyInvites({
      serverUrl,
      account_id,
      survey_id: surveyId,
      user_id,
      emails: unique,
      pending_only: true,
    });
    clearInvitationSelection();
    reloadInvitationsAfterSend();
    return data;
  }

  function confirmResendInvitations(invitationsToResend) {
    const list = (invitationsToResend || []).filter(isPendingInvitation);
    if (list.length === 0) return;
    const emails = list.map((inv) => inv.email).filter(Boolean);
    if (emails.length === 0) {
      showSendResultMessage("Selected invitation(s) have no email address.");
      return;
    }
    const isSingle = list.length === 1;
    const label = list[0].respondent_display || list[0].email || "recipient";
    setConfirmDialog({
      open: true,
      title: isSingle ? "Resend invite" : "Resend invites",
      message: isSingle
        ? `Resend invite to ${label}?`
        : `Resend invites to ${list.length} pending respondents?`,
      mode: "confirm",
      confirmLabel: isSingle ? "Resend" : `Resend (${list.length})`,
      onConfirm: async () => {
        setResendLoadingId(isSingle ? list[0]._id : "bulk");
        try {
          const data = await sendResendInvites(emails);
          closeConfirmDialog();
          showSendResultMessage((data && data.message) || "Invites processed.");
        } catch (err) {
          closeConfirmDialog();
          showSendResultMessage(getMessageForFetchError(err, "Failed to re-send invites."));
        } finally {
          setResendLoadingId(null);
        }
      },
    });
  }

  function handleIndividualSort(key) {
    setIndividualSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  }

  function exportIndividualResponsesCsv() {
    const rows = sortedIndividualResponses;
    if (!rows.length) {
      setConfirmDialog({
        open: true,
        title: "Export",
        message: "No individual responses to export.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setExportingIndividualCsv(true);
    try {
      const csv = buildSurveyIndividualResponsesCsvExportText(
        rows,
        individualResponsesQuestions
      );
      const datePart = new Date().toISOString().slice(0, 10);
      downloadCsvTextFromString(`survey-individual-responses-export-${datePart}.csv`, csv);
    } catch (err) {
      setConfirmDialog({
        open: true,
        title: "Export",
        message: getMessageForFetchError(err, "Error exporting CSV."),
        mode: "alert",
        onConfirm: null,
      });
    } finally {
      setExportingIndividualCsv(false);
    }
  }

  const sortedIndividualResponses = (() => {
    if (!individualSort.key || individualResponses.length === 0) return individualResponses;
    const list = [...individualResponses];
    list.sort((a, b) => {
      let aVal, bVal;
      if (individualSort.key === "respondent_display") {
        aVal = (a.respondent_display ?? a.email ?? "").toString().toLowerCase();
        bVal = (b.respondent_display ?? b.email ?? "").toString().toLowerCase();
      } else if (individualSort.key === "responded_at") {
        aVal = a.responded_at ? new Date(a.responded_at).getTime() : 0;
        bVal = b.responded_at ? new Date(b.responded_at).getTime() : 0;
      } else if (typeof individualSort.key === "number" || String(individualSort.key).startsWith("answer_")) {
        const idx = typeof individualSort.key === "number" ? individualSort.key : parseInt(String(individualSort.key).replace("answer_", ""), 10);
        aVal = (a.answers && a.answers[idx] ? a.answers[idx].answer : "").toString().toLowerCase();
        bVal = (b.answers && b.answers[idx] ? b.answers[idx].answer : "").toString().toLowerCase();
      } else return 0;
      if (aVal < bVal) return individualSort.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return individualSort.direction === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  })();

  const containerStyle = {
    maxWidth: "1100px",
    margin: "0 auto",
    padding: "16px",
    fontFamily:
      "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  };

  const tabButtonBase = {
    padding: "6px 14px",
    fontSize: "13px",
    borderRadius: "999px",
    border: "1px solid #dee2e6",
    cursor: "pointer",
    background: "#fff",
    color: "#495057",
  };

  function renderQuestionsTab() {
    if (loadingSurvey) {
      return <p style={{ fontSize: "13px", color: "#6c757d" }}>Loading questions…</p>;
    }
    if (surveyError) {
      return (
        <p style={{ fontSize: "13px", color: "#842029" }}>
          {surveyError}
        </p>
      );
    }
    if (questions.length === 0) {
      return (
        <p style={{ fontSize: "13px", color: "#6c757d" }}>
          No questions defined yet for this survey.
        </p>
      );
    }
    return (
      <ol style={{ margin: "0", paddingLeft: "18px", fontSize: "13px", color: "#212529" }}>
        {questions.map((q, idx) => {
          const typeLabel = TYPE_LABELS[q.question_type] || q.question_type || "Question";
          const options = Array.isArray(q.options) ? q.options.filter((o) => o != null && String(o).trim() !== "") : [];
          const hasScale =
            (q.question_type === "rating" || q.question_type === "nps") &&
            q.scale_min != null &&
            q.scale_max != null;
          return (
            <li key={q._id || idx} style={{ marginBottom: "10px" }}>
              <div style={{ fontWeight: 600 }}>{q.question_text || "(No question text)"}</div>
              <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "2px" }}>
                {typeLabel}
                {hasScale && (
                  <>
                    {" · Scale: "}
                    {q.scale_min}–{q.scale_max}
                  </>
                )}
              </div>
              {options.length > 0 && (
                <div style={{ marginTop: "4px", fontSize: "12px", color: "#495057" }}>
                  <span style={{ fontWeight: 600 }}>Options:</span>{" "}
                  {options.join(", ")}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    );
  }

  function renderInvitationsTab() {
    if (!invitationsLoaded && loadingInvitations) {
      return <p style={{ fontSize: "13px", color: "#6c757d" }}>Loading invitations…</p>;
    }
    if (invitationError) {
      return (
        <p style={{ fontSize: "13px", color: "#842029" }}>
          {invitationError}
        </p>
      );
    }
    if (invitations.length === 0) {
      return (
        <div>
          <p style={{ fontSize: "13px", color: "#6c757d", marginBottom: "10px" }}>
            No invitations have been sent yet for this survey.
          </p>
          <button
            type="button"
            onClick={() => setSendDialogOpen(true)}
            className="btn btn-primary btn-md"
          >
            + Invite New
          </button>
        </div>
      );
    }
    return (
      <>
        <div className="toolbar">
          <span className="toolbar-label">Status:</span>
          <select
            value={invitationStatusFilter}
            onChange={(e) => {
              setInvitationStatusFilter(e.target.value);
              clearInvitationSelection();
            }}
            className="input input-min-120"
            aria-label="Status filter"
          >
            <option value="">All</option>
            <option value="Pending">Pending</option>
            <option value="Completed">Completed</option>
          </select>
          <span className="toolbar-divider" />
          <button
            type="button"
            onClick={() => setSendDialogOpen(true)}
            className="btn btn-primary btn-md"
          >
            + Invite New
          </button>
        </div>
        {selectedPendingInvitations.length > 0 && (
          <div className="toolbar" role="toolbar" aria-label="Bulk invitation actions">
            <span className="toolbar-label">{selectedPendingInvitations.length} selected</span>
            <button
              type="button"
              onClick={() => confirmResendInvitations(selectedPendingInvitations)}
              disabled={resendBusy}
              className="btn btn-primary"
            >
              {resendLoadingId === "bulk"
                ? "Sending…"
                : `Resend Invites (${selectedPendingInvitations.length})`}
            </button>
            <button
              type="button"
              onClick={clearInvitationSelection}
              disabled={resendBusy}
              className="btn btn-secondary"
            >
              Clear selection
            </button>
          </div>
        )}
        <div style={{ border: "1px solid #dee2e6", borderRadius: "8px", overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
              minWidth: "800px",
            }}
          >
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}>
                <th className="text-center" style={{ padding: "8px 10px", width: 40, minWidth: 40 }}>
                  <input
                    type="checkbox"
                    checked={allVisiblePendingSelected}
                    onChange={toggleSelectAllPendingVisible}
                    disabled={visiblePendingInvitations.length === 0 || resendBusy}
                    title="Select all pending"
                    aria-label="Select all pending invitations"
                  />
                </th>
                <th style={{ padding: "8px 10px", textAlign: "left", cursor: "pointer" }} onClick={() => handleInvitationsSort("respondent_display")}>Respondent</th>
                <th style={{ padding: "8px 10px", textAlign: "left", cursor: "pointer" }} onClick={() => handleInvitationsSort("status")}>Status</th>
                <th style={{ padding: "8px 10px", textAlign: "left", cursor: "pointer" }} onClick={() => handleInvitationsSort("sent_at")}>Sent at</th>
                <th style={{ padding: "8px 10px", textAlign: "left", cursor: "pointer" }} onClick={() => handleInvitationsSort("responded_at")}>Responded at</th>
                <TableActionColumnHeader align="left" style={{ padding: "8px 10px", width: 56 }} />
              </tr>
            </thead>
            <tbody>
              {sortedInvitations.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: "12px 10px", color: "#6c757d" }}>
                    No invitations match this status filter.
                  </td>
                </tr>
              ) : (
                sortedInvitations.map((inv) => {
                  const pending = isPendingInvitation(inv);
                  return (
                    <tr key={inv._id} style={{ borderBottom: "1px solid #f1f3f5" }}>
                      <td className="text-center" style={{ padding: "8px 10px" }}>
                        {pending ? (
                          <input
                            type="checkbox"
                            checked={selectedInvitationIds.includes(inv._id)}
                            onChange={() => toggleInvitationSelected(inv._id)}
                            disabled={resendBusy}
                            aria-label={`Select ${inv.respondent_display || displayEmailOrDash(inv.email) || "invitation"}`}
                          />
                        ) : null}
                      </td>
                      <td style={{ padding: "8px 10px", fontWeight: 500 }} title={inv.email && !isPlaceholderEmail(inv.email) && inv.email !== (inv.respondent_display || "") ? inv.email : undefined}>
                        {inv.respondent_display ?? displayEmailOrDash(inv.email)}
                      </td>
                      <td style={{ padding: "8px 10px" }}>{invitationStatusOf(inv)}</td>
                      <td style={{ padding: "8px 10px" }}>
                        {inv.sent_at ? dayjs(inv.sent_at).format("MM/DD/YYYY HH:mm") : "—"}
                      </td>
                      <td style={{ padding: "8px 10px" }}>
                        {inv.responded_at ? dayjs(inv.responded_at).format("MM/DD/YYYY HH:mm") : "—"}
                      </td>
                      <td style={{ padding: "8px 10px" }}>
                        {pending ? (
                          <RowActionIcons>
                            <button
                              type="button"
                              onClick={() => confirmResendInvitations([inv])}
                              disabled={resendBusy}
                              className="btn-link"
                              style={iconBtnStyle}
                              title="Resend"
                            >
                              {resendLoadingId === inv._id ? "…" : <IconInvite size={18} />}
                            </button>
                          </RowActionIcons>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  function handleInvitesSent(data) {
    clearInvitationSelection();
    reloadInvitationsAfterSend();
    showSendResultMessage((data && data.message) || "Invites processed.");
  }

  function renderResultsTab() {
    return (
      <SurveyResultsContent
        survey={survey}
        results={results}
        totalResponses={totalResponses}
        loading={!resultsLoaded && loadingResults}
        error={resultsError}
        embedded
      />
    );
  }

  function renderIndividualResponsesTab() {
    if (!individualResponsesLoaded && loadingIndividualResponses) {
      return <p style={{ fontSize: "13px", color: "#6c757d" }}>Loading individual responses…</p>;
    }
    if (individualResponsesError) {
      return (
        <p style={{ fontSize: "13px", color: "#842029" }}>
          {individualResponsesError}
        </p>
      );
    }
    if (individualResponses.length === 0) {
      return (
        <p style={{ fontSize: "13px", color: "#6c757d" }}>
          No responses yet. Individual answers will appear here once respondents submit the survey.
        </p>
      );
    }
    const cols = individualResponsesQuestions;
    return (
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "13px",
            minWidth: "600px",
          }}
        >
          <thead>
            <tr style={{ background: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}>
              <th style={{ padding: "8px 10px", textAlign: "left", whiteSpace: "nowrap", cursor: "pointer" }} onClick={() => handleIndividualSort("respondent_display")}>Respondent</th>
              <th style={{ padding: "8px 10px", textAlign: "left", whiteSpace: "nowrap", cursor: "pointer" }} onClick={() => handleIndividualSort("responded_at")}>Responded at</th>
              {cols.map((q, qIdx) => (
                <th
                  key={q._id}
                  style={{
                    padding: "8px 10px",
                    textAlign: "left",
                    maxWidth: "200px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  title={q.question_text}
                  onClick={() => handleIndividualSort(`answer_${qIdx}`)}
                >
                  {(q.question_text || "").slice(0, 40)}
                  {(q.question_text || "").length > 40 ? "…" : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedIndividualResponses.map((row) => (
              <tr key={row.response_id} style={{ borderBottom: "1px solid #f1f3f5" }}>
                <td style={{ padding: "8px 10px", fontWeight: 500 }} title={row.email && !isPlaceholderEmail(row.email) && row.email !== (row.respondent_display || "") ? row.email : undefined}>
                  {row.respondent_display ?? displayEmailOrDash(row.email)}
                </td>
                <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                  {row.responded_at ? dayjs(row.responded_at).format("MM/DD/YYYY HH:mm") : "—"}
                </td>
                {row.answers.map((a, aIdx) => {
                  const q = cols[aIdx] || {};
                  const sentiment = a.sentiment;
                  const showBadge = Boolean(sentiment);
                  const badgeStyle = (() => {
                    if (sentiment === "positive") return { background: "var(--sentiment-positive)", color: "#fff" };
                    if (sentiment === "negative") return { background: "var(--sentiment-negative)", color: "#1f2937" };
                    return { background: "var(--sentiment-neutral)", color: "#fff" };
                  })();
                  return (
                    <td
                      key={a.question_id}
                      style={{
                        padding: "8px 10px",
                        maxWidth: "200px",
                        wordBreak: "break-word",
                        verticalAlign: "top",
                      }}
                      title={a.answer}
                    >
                      {showBadge && (
                        <span
                          style={{
                            display: "inline-block",
                            fontSize: "11px",
                            fontWeight: 600,
                            padding: "2px 6px",
                            borderRadius: "4px",
                            marginBottom: "4px",
                            textTransform: "capitalize",
                            ...badgeStyle,
                          }}
                        >
                          {sentiment}
                        </span>
                      )}
                      {showBadge && <br />}
                      {a.answer || "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const loadingTopLevel = loadingSurvey && !survey;

  return (
    <div style={containerStyle}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          padding: "4px 0",
          marginBottom: "8px",
          border: "none",
          background: "none",
          color: "#0d6efd",
          textDecoration: "underline",
          cursor: "pointer",
          fontSize: "13px",
        }}
      >
        ← Back
      </button>

      <div
        style={{
          borderRadius: "12px",
          border: "1px solid #dee2e6",
          padding: "14px 18px",
          background: "#f8f9fa",
          marginBottom: "12px",
        }}
      >
        {loadingTopLevel ? (
          <p style={{ fontSize: "14px", color: "#6c757d", margin: 0 }}>
            Loading survey…
          </p>
        ) : survey ? (
          <>
            <h2
              style={{
                margin: "0 0 4px 0",
                fontSize: "1.3rem",
                fontWeight: 600,
                color: "#212529",
              }}
            >
              Survey Details – {survey.name || "Survey"}
            </h2>
            {survey.description && (
              <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#495057" }}>
                {survey.description}
              </p>
            )}
            <div style={{ marginTop: "6px", fontSize: "12px", color: "#6c757d" }}>
              {survey.start_date && survey.end_date && (
                <span>
                  Open from{" "}
                  {dayjs(survey.start_date).format("MMM D, YYYY")} to{" "}
                  {dayjs(survey.end_date).format("MMM D, YYYY")}.{" "}
                </span>
              )}
              <span>
                Status: <strong>{survey.status || "Open"}</strong>
              </span>
            </div>
          </>
        ) : (
          <p style={{ fontSize: "14px", color: "#842029", margin: 0 }}>
            {surveyError || "Survey not found."}
          </p>
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "10px",
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={() => setActiveTab("questions")}
          style={{
            ...tabButtonBase,
            background: activeTab === "questions" ? "#0d6efd" : "#fff",
            color: activeTab === "questions" ? "#fff" : "#495057",
            borderColor: activeTab === "questions" ? "#0d6efd" : "#dee2e6",
          }}
        >
          Questions ({questionsCount})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("invitations")}
          style={{
            ...tabButtonBase,
            background: activeTab === "invitations" ? "#0d6efd" : "#fff",
            color: activeTab === "invitations" ? "#fff" : "#495057",
            borderColor: activeTab === "invitations" ? "#0d6efd" : "#dee2e6",
          }}
        >
          Invitations ({invitationsCount})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("results")}
          style={{
            ...tabButtonBase,
            background: activeTab === "results" ? "#0d6efd" : "#fff",
            color: activeTab === "results" ? "#fff" : "#495057",
            borderColor: activeTab === "results" ? "#0d6efd" : "#dee2e6",
          }}
        >
          Results ({totalResponses})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("individual")}
          style={{
            ...tabButtonBase,
            background: activeTab === "individual" ? "#0d6efd" : "#fff",
            color: activeTab === "individual" ? "#fff" : "#495057",
            borderColor: activeTab === "individual" ? "#0d6efd" : "#dee2e6",
          }}
        >
          Individual responses ({totalResponses})
        </button>
        {activeTab === "individual" ? (
          <button
            type="button"
            className="btn btn-md btn-primary-outline"
            style={{ marginLeft: "auto" }}
            disabled={individualResponses.length === 0 || exportingIndividualCsv}
            onClick={() => exportIndividualResponsesCsv()}
          >
            {exportingIndividualCsv
              ? "Exporting…"
              : `Export CSV${individualResponses.length > 0 ? ` (${individualResponses.length})` : ""}`}
          </button>
        ) : null}
      </div>

      <div
        style={{
          borderRadius: "10px",
          border: "1px solid #dee2e6",
          padding: "12px 14px",
          background: "#fff",
          minHeight: "120px",
        }}
      >
        {activeTab === "questions" && renderQuestionsTab()}
        {activeTab === "invitations" && renderInvitationsTab()}
        {activeTab === "results" && renderResultsTab()}
        {activeTab === "individual" && renderIndividualResponsesTab()}
      </div>

      <ConfirmationModal
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        mode={confirmDialog.mode || "alert"}
        confirmLabel={confirmDialog.confirmLabel || (confirmDialog.mode === "confirm" ? "Confirm" : "OK")}
        onConfirm={() => {
          if (typeof confirmDialog.onConfirm === "function") {
            return confirmDialog.onConfirm();
          }
          closeConfirmDialog();
        }}
        onCancel={closeConfirmDialog}
      />

      <SmartInvitationModal
        open={sendDialogOpen}
        title={`Send survey – ${survey?.name || "Survey"}`}
        account_id={account_id}
        survey_id={surveyId}
        user_id={user_id}
        onClose={() => setSendDialogOpen(false)}
        onSent={handleInvitesSent}
      />
    </div>
  );
}

export default SurveyDetails;

